import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Globe, TrendingUp, Award, Menu, X, Building2, GraduationCap, Trophy, Users, Briefcase, MapPin, FileText, Heart } from "lucide-react";
import { useWalkthru } from "@/context/Walkthru";
import Seo from "@/components/Seo";
import { PaymentModal } from "@/components/PaymentModal";
import { track } from "@/lib/track";
import { EnquiryModal } from "@/components/EnquiryModal";
import { TestimonialsSection } from "@/components/Testimonials";
import { SocialLinks } from "@/components/SocialLinks";
import { LogoShowcase } from "@/components/LogoShowcase";

const BANNER_IMG = "/top100-poster.jpg";
const BOOKING_ENABLED = true;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUDIENCE_ICONS = [GraduationCap, Users, Briefcase];

const FRAMEWORK = [
  { letter: "C", word: "Clarify", desc: "Define what you actually want: role, industry, geography, and the life around the work." },
  { letter: "A", word: "Assess", desc: "A detailed diagnostic report that maps your strengths across six aptitude dimensions." },
  { letter: "R", word: "Reposition", desc: "Rewrite your narrative, your resume, LinkedIn, and story, around evidence, not duties." },
  { letter: "E", word: "Equip", desc: "Build the interview, negotiation, and communication toolkit your target role demands." },
  { letter: "E", word: "Execute", desc: "A week by week action plan with accountability checkpoints and real applications." },
  { letter: "R", word: "Refine", desc: "Review outcomes, sharpen the strategy, and compound what is working." },
];

const TIERS = [
  {
    key: "session",
    name: "One Time Session",
    bestFor: "Best for college students & fresh graduates",
    inr: "₹4,999",
    usd: "$60",
    per: "one time",
    desc: "A single, focused one hour strategy session to unblock one decision: pivot, promotion, or first role.",
    features: ["60 minute 1:1 session", "Six dimension aptitude scorecard", "Written action summary"],
    cta: "Book a session",
  },
  {
    key: "membership",
    name: "Premium / Monthly Plan",
    bestFor: "Best for junior professionals & fresh graduates",
    inr: "₹9,999",
    usd: "$199",
    per: "per month",
    desc: "Five 1:1 sessions each month, one hour each, for professionals building real momentum. Includes everything in the One Time (one hour) session.",
    features: ["Five 1:1 sessions each month", "Everything in the One Time session", "Resume review", "Interview preparation", "Monthly action plan"],
    cta: "Start membership",
    featured: true,
    recurring: true,
  },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });


const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const Chapter = ({ num, label }) => (
  <div className="flex items-end gap-6 mb-12">
    <span className="font-head outline-num text-7xl sm:text-8xl font-black leading-none">{num}</span>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833] pb-3">{label}</p>
  </div>
);

export default function Landing() {
  const [activeTier, setActiveTier] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const { start: startWalkthru } = useWalkthru();
  const [content, setContent] = useState(null);
  useEffect(() => {
    axios.get(`${API}/site-content`).then((r) => setContent(r.data)).catch(() => {});
  }, []);
  const audiences = content?.audiences?.length ? content.audiences : [
    { title: "College students", points: ["Career direction", "Internships", "Profile building", "Study-abroad planning"] },
    { title: "Fresh graduates", points: ["Employability", "Resume / LinkedIn", "Job search", "Interviews"] },
    { title: "Junior professionals", points: ["Career switches", "Promotions", "Confidence", "Personal branding"] },
  ];
  const inst = content?.institutional || {
    heading: "Career readiness programs for teams and cohorts.",
    description: "Tailored programs for institutes, ed tech companies, skill development organizations and corporate teams. Includes structured career clarity, positioning, interview preparation and the Career Discovery Assessment, delivered at scale for your learners.",
    bullets: ["Institutes & universities", "Ed tech platforms", "Skill development bodies", "Corporate L&D teams"],
    note: "Final scope and pricing are shared once we understand your cohort size and goals. Sessions begin this October.",
  };
  const priceFor = (key) => ({
    inr: content?.prices?.[key]?.inr || TIERS.find((t) => t.key === key)?.inr,
    usd: content?.prices?.[key]?.usd ?? TIERS.find((t) => t.key === key)?.usd,
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const goSection = (id) => { setMenuOpen(false); scrollTo(id); };

  return (
    <div data-testid="landing-page">
      <Seo
        title="The Next Move by Rhellex | Career Strategy & Growth Mentorship"
        siteName="The Next Move by Rhellex"
        description="Career clarity, mentorship, and an executable strategy for your next move. 1:1 mentorship for students and early career professionals by Rhea Sharma, PMP® and MBA."
        image="/og-image.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "The Next Move by Rhellex",
          description: "Career strategy and professional growth mentorship for students and early career professionals.",
          founder: { "@type": "Person", name: "Rhea Sharma" },
        }}
      />

      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 font-head font-black text-base sm:text-lg tracking-tight">
            <img src="/logo-mark.png" alt="The Next Move by Rhellex" className="h-8 w-8 object-contain" />
            <span>The Next Move<span className="text-[#D94833]"> by Rhellex</span></span>
          </button>
          <nav className="hidden md:flex items-center gap-0.5 text-sm font-medium text-[#3A4A63] bg-[#041C3E]/[0.04] border border-[#041C3E]/10 rounded-full px-1.5 py-1.5">
            <a data-testid="nav-about-link" href="/about" className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">About</a>
            <button data-testid="nav-framework-link" onClick={() => scrollTo("framework")} className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Framework</button>
            <button data-testid="nav-pricing-link" onClick={() => scrollTo("pricing")} className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Services</button>
            <a data-testid="nav-testimonials-link" href="/testimonials" className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Testimonials</a>
            <a data-testid="nav-achievements-link" href="/achievements" className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Achievements</a>
            <a data-testid="nav-blog-link" href="/blog" className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Blog</a>
            <a data-testid="nav-login-link" href="/login" className="px-4 py-2 rounded-full hover:bg-white hover:text-[#041C3E] hover:shadow-sm transition-all duration-200">Login</a>
          </nav>
          <button
            data-testid="nav-cta-button"
            onClick={() => { track("Navigation", "Clicked Donate to Begin"); scrollTo("pricing"); }}
            className="hidden md:inline-flex bg-[#D94833] text-white px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300"
          >
            See services
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden text-[#041C3E] p-2 -mr-2"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden border-t border-[#E5E7EB] bg-white px-6 py-4 flex flex-col gap-1"
          >
            <a data-testid="mobile-nav-about" href="/about" className="py-3 text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">About</a>
            <button data-testid="mobile-nav-framework" onClick={() => goSection("framework")} className="py-3 text-left text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Framework</button>
            <button data-testid="mobile-nav-services" onClick={() => goSection("pricing")} className="py-3 text-left text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Services</button>
            <a data-testid="mobile-nav-testimonials" href="/testimonials" className="py-3 text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Testimonials</a>
            <a data-testid="mobile-nav-achievements" href="/achievements" className="py-3 text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Achievements</a>
            <a data-testid="mobile-nav-blog" href="/blog" className="py-3 text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Blog</a>
            <a data-testid="mobile-nav-login" href="/login" className="py-3 text-base font-semibold text-[#041C3E] border-b border-[#F3F4F6]">Login</a>
            <button
              data-testid="mobile-nav-cta"
              onClick={() => goSection("pricing")}
              className="mt-3 bg-[#D94833] text-white px-5 py-3 text-base font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300"
            >
              See services
            </button>
          </motion.nav>
        )}
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-12 gap-8 items-center w-full">
          <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 relative z-10">
            <motion.div
              data-testid="launch-banner"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-3 mb-7 border border-[#041C3E]/15 bg-[#041C3E]/[0.03] pl-3 pr-1 py-1"
            >
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#041C3E]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D94833] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D94833]" />
                </span>
                Sessions open soon
              </span>
              <button
                data-testid="launch-banner-cta"
                onClick={() => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="bg-[#D94833] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#C03F2A] transition-colors duration-300"
              >
                Join early access
              </button>
            </motion.div>
            <MaskedLine delay={0.1}>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#3A4A63]">
                Career Strategy · Rhea Sharma, PMP® · MBA
              </span>
            </MaskedLine>
            <h1 className="font-head text-6xl sm:text-7xl lg:text-[6rem] leading-[0.9] tracking-tighter font-black mt-6">
              <MaskedLine delay={0.25}>Turn potential</MaskedLine>
              <MaskedLine delay={0.4}>into a career</MaskedLine>
              <MaskedLine delay={0.55} className="text-[#D94833]">strategy.</MaskedLine>
            </h1>
            <MaskedLine delay={0.75}>
              <span className="block text-base md:text-lg leading-relaxed text-[#3A4A63] max-w-md mt-8">
                1:1 mentorship for students and early-career professionals, and group coaching and training for corporates and college campuses who want a plan, not platitudes.
                Structured diagnostics, honest positioning, and accountable execution.
              </span>
            </MaskedLine>
            <motion.div
              className="flex flex-wrap gap-4 mt-10"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                data-testid="hero-primary-cta"
                onClick={() => { track("Navigation", "Clicked Book a Session"); scrollTo("pricing"); }}
                className="bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300 flex items-center gap-2"
              >
                See programs <ArrowUpRight size={18} />
              </button>
              <button
                data-testid="hero-secondary-cta"
                onClick={() => scrollTo("framework")}
                className="border border-[#041C3E] text-[#041C3E] px-8 py-4 font-semibold tracking-wide hover:bg-[#041C3E] hover:text-white transition-colors duration-300"
              >
                The CAREER™ Framework
              </button>
            </motion.div>
            <div
              data-testid="hero-trust-badges"
              className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-10 pt-8 border-t border-[#E5E7EB]"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Credentials</span>
              {[
                { label: "PMP® Certified", icon: Award },
                { label: "MBA", icon: GraduationCap },
                { label: "Top 100 DAP Professional", icon: Trophy },
              ].map((b) => (
                <span key={b.label} data-testid={`trust-badge-${b.label.split(" ")[0].toLowerCase()}`}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#041C3E]">
                  <b.icon size={15} className="text-[#D94833]" /> {b.label}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <LogoShowcase />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Editorial marquee */}
      <div className="border-y border-[#E5E7EB] py-5 overflow-hidden">
        <Marquee speed={30} gradient={false} data-testid="editorial-marquee">
          {["Strategy", "Clarity", "Growth", "Leadership", "Execution", "Positioning"].map((w) => (
            <span key={w} className="font-head text-2xl font-bold uppercase tracking-[0.2em] text-[#041C3E]/20 mx-8">
              {w} <span className="text-[#D94833]/40 mx-4">·</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* 01 About / Authority */}
      <section id="about" className="py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-6">
          <Chapter num="01" label="About the mentor" />
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <img
                  src={BANNER_IMG}
                  alt="Rhea Sharma, recognized as a Top 100 DAP Professional in 2024 and 2026, DAP Specialist at Accenture"
                  className="w-full h-auto border border-[#E5E7EB]"
                />
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <Reveal>
                <h2 className="font-head text-4xl sm:text-5xl leading-[1.1] tracking-tight font-bold">
                  Life lessons and Corporate discipline, applied to your career.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-base md:text-lg leading-relaxed text-[#3A4A63] mt-6">
                  Rhea Sharma spent 7+ years in Digital Adoption (DAP) and Learning &amp; Development,
                  driving change for corporate clients across the U.S., Europe, India, and Asia Pacific, with
                  programs that lifted adoption by more than 80%. She now applies the same structured, measurable
                  approach to individual careers, regardless of your role, title, or industry.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mt-10">
                  {[
                    { icon: Award, stat: "PMP® · MBA", label: "Certified & credentialed" },
                    { icon: Globe, stat: "Global", label: "U.S. · Europe · India · Asia Pacific" },
                    { icon: TrendingUp, stat: "7+ years", label: "DAP & L&D leadership" },
                  ].map(({ icon: Icon, stat, label }) => (
                    <div key={stat} className="bg-white p-6">
                      <Icon size={18} className="text-[#D94833]" />
                      <p className="font-head text-xl font-bold mt-3">{stat}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1 uppercase tracking-[0.1em]">{label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <a
                  data-testid="about-read-more-link"
                  href="/about"
                  className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-[#041C3E] border-b-2 border-[#D94833] pb-1 hover:text-[#D94833] transition-colors"
                >
                  Read Rhea's full story <ArrowUpRight size={16} />
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 bg-[#041C3E] p-5 sm:p-8" data-testid="about-dap-certifications">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C69A52]">Certified Digital Adoption Consultant and Advisor</p>
              <img
                src="/dap-profile-1.png"
                alt="Rhea Sharma, WalkMe Certified Digital Adoption Consultant and Advisor, holding Program Manager, DAP Consultant, DAP Advisor, DAP Delivery Consultant, DAP Delivery Engineer, Builder and Fundamentals certifications, plus Whatfix Technology Advocate and Oracle Foundations Associate"
                className="w-full h-auto mt-4"
              />
              <p className="text-xs text-white/60 mt-4 uppercase tracking-[0.15em]">
                WalkMe, Whatfix and Oracle credentials
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 02 Framework */}
      <section id="framework" className="py-32 lg:py-40 bg-[#041C3E] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end gap-6 mb-4">
            <span className="font-head text-7xl sm:text-8xl font-black leading-none" style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.9)", color: "transparent" }}>02</span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833] pb-3">The methodology</p>
          </div>
          <Reveal>
            <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-bold max-w-2xl">
              The CAREER™ Framework
            </h2>
          </Reveal>
          <div className="mt-16">
            {FRAMEWORK.map((step, i) => (
              <Reveal key={step.word} delay={i * 0.05}>
                <div
                  data-testid={`framework-step-${step.word.toLowerCase()}`}
                  className="border-t border-white/15 py-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline group"
                >
                  <span className="md:col-span-2 font-head text-6xl font-black text-white/15 group-hover:text-[#D94833] transition-colors duration-500">
                    {step.letter}
                  </span>
                  <h3 className="md:col-span-3 font-head text-2xl sm:text-3xl font-semibold">{step.word}</h3>
                  <p className="md:col-span-7 text-white/60 leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 Pricing */}
      <section id="pricing" className="py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-6">
          <Chapter num="03" label="Services & pricing" />

          {/* Who this is for (now first) */}
          <Reveal delay={0.02}>
            <div data-testid="who-this-is-for" className="mt-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Who this is for</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mt-6">
                {audiences.map((group, gi) => {
                  const Icon = AUDIENCE_ICONS[gi] || GraduationCap;
                  return (
                  <div key={group.title} data-testid={`audience-${group.title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white p-8 flex flex-col">
                    <Icon size={28} className="text-[#D94833]" />
                    <h3 className="font-head text-xl font-bold mt-4">{group.title}</h3>
                    <ul className="mt-4 space-y-2">
                      {group.points.map((p) => (
                        <li key={p} className="text-sm text-[#3A4A63] flex gap-2"><span className="text-[#D94833]">·</span> {p}</li>
                      ))}
                    </ul>
                  </div>
                  );
                })}
              </div>
              <p className="font-head text-xl sm:text-2xl font-bold text-[#041C3E] mt-8">
                {content?.audience_closing || "If you're serious about your next move, let's make it intentional."}
              </p>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-bold max-w-3xl mt-20">
              Choose your pace. Every path starts with clarity.
            </h2>
          </Reveal>
          <Reveal delay={0.045}>
            <p data-testid="donate-tagline" className="mt-6 font-head text-xl sm:text-2xl font-bold text-[#041C3E] max-w-3xl">
              Donate to KGSKF to begin your session and start your journey with us!
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p data-testid="services-contact-note" className="mt-4 text-sm md:text-base text-[#3A4A63] max-w-3xl">
              [To get started immediately contact Rhea Sharma on{" "}
              <a href="mailto:thenextmovebyrhellex@gmail.com" className="font-semibold text-[#041C3E] underline underline-offset-2 hover:text-[#D94833]">
                thenextmovebyrhellex@gmail.com
              </a>{" "}
              *<a href="/terms" target="_blank" rel="noreferrer" className="font-bold text-[#041C3E] underline underline-offset-2 hover:text-[#D94833]">t&c</a> applied]
            </p>
          </Reveal>

          <Reveal delay={0.06}>
            <div className="mt-8 space-y-3">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  data-testid="walkthru-launcher"
                  onClick={() => startWalkthru("booking")}
                  className="inline-flex items-center gap-2 bg-[#FBEFEA] border border-[#D94833]/40 text-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#D94833] hover:text-white transition-colors group"
                >
                  <MapPin size={16} className="text-[#D94833] group-hover:text-white" />
                  How to book your 1:1 session
                </button>
                <button
                  data-testid="corporate-walkthru-launcher"
                  onClick={() => startWalkthru("corporate")}
                  className="inline-flex items-center gap-2 bg-[#FBEFEA] border border-[#D94833]/40 text-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#D94833] hover:text-white transition-colors group"
                >
                  <Building2 size={16} className="text-[#D94833] group-hover:text-white" />
                  How to submit a corporate inquiry
                </button>
                <button
                  data-testid="impact-walkthru-launcher"
                  onClick={() => startWalkthru("impact")}
                  className="inline-flex items-center gap-2 bg-[#FBEFEA] border border-[#D94833]/40 text-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#D94833] hover:text-white transition-colors group"
                >
                  <Heart size={16} className="text-[#D94833] group-hover:text-white" />
                  Our Impact: Where your donation goes
                </button>
              </div>
              <a
                data-testid="impact-qrg-link"
                href="/donation-tax-benefits-qrg.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#3A4A63] underline underline-offset-4 decoration-[#D94833]/40 hover:text-[#041C3E] transition-colors"
              >
                <FileText size={14} className="text-[#D94833]" />
                Donation &amp; Tax Benefits QRG
              </a>
            </div>
          </Reveal>

          <div data-testid="pricing-plans-grid" id="plans" className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mt-16">
            {TIERS.map((tier, i) => (
              <Reveal key={tier.key} delay={i * 0.07} className="h-full">
                <div
                  data-testid={`pricing-card-${tier.key}`}
                  className={`bg-white p-8 h-full flex flex-col hover:border-[#041C3E] transition-colors duration-300 ${tier.featured ? "border-2 border-[#041C3E]" : ""}`}
                >
                  {tier.featured && (
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833] mb-4">Most chosen</p>
                  )}
                  <h3 className="font-head text-2xl font-bold">{tier.name}</h3>
                  {tier.bestFor && (
                    <p data-testid={`pricing-bestfor-${tier.key}`} className="text-xs font-semibold text-[#D94833] mt-1">{tier.bestFor}</p>
                  )}
                  <div className="mt-4 flex items-baseline gap-2" data-testid={`pricing-amount-${tier.key}`}>
                    <span className="font-head text-4xl font-black">{priceFor(tier.key).inr}</span>
                    {priceFor(tier.key).usd && <span className="text-[#9CA3AF] text-sm">/ {priceFor(tier.key).usd}</span>}
                  </div>
                  {tier.per && <p className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em] mt-1">{tier.per}</p>}
                  {tier.recurring && (
                    <p data-testid="pricing-recurring-badge" className="inline-block mt-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#D94833] border border-[#D94833] px-2 py-1">
                      Renews monthly · cancel anytime
                    </p>
                  )}
                  <p className="text-sm text-[#3A4A63] leading-relaxed mt-4">{tier.desc}</p>
                  <ul className="mt-6 space-y-2 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="text-sm text-[#3A4A63] flex gap-2">
                        <span className="text-[#D94833]">·</span> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`pricing-cta-${tier.key}`}
                    onClick={() => { if (BOOKING_ENABLED) { track("Navigation", `Selected Tier: ${tier.key}`); setActiveTier({ ...tier, ...priceFor(tier.key) }); } }}
                    disabled={!BOOKING_ENABLED}
                    className={`mt-8 w-full px-8 py-4 font-semibold tracking-wide transition-colors duration-300 ${
                      BOOKING_ENABLED
                        ? "bg-[#D94833] text-white hover:bg-[#C03F2A]"
                        : "bg-[#F3F4F6] text-[#9CA3AF] border border-[#E5E7EB] cursor-not-allowed"
                    }`}
                  >
                    {BOOKING_ENABLED ? "Donate to start your session" : "Available this October"}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div data-testid="payment-no-refund-disclaimer" className="mt-8 border-l-4 border-[#D94833] bg-[#FBEDEA] px-5 py-4">
              <p className="text-sm md:text-base font-semibold text-[#041C3E] leading-relaxed">
                {content?.refund_banner || "Your full donation is required in advance to reserve your session. You may cancel for a full refund within 24 hours of your donation, and sessions can be rescheduled up to 24 hours before the start time. Any cancellation made after 24 hours from booking, or less than 36 hours before the session date, will receive only a partial refund."}
              </p>
            </div>
          </Reveal>
          <p className="text-xs text-[#9CA3AF] mt-6">
            {BOOKING_ENABLED
              ? "Your donation is processed securely in INR via Razorpay. Your client intake and Career Discovery Assessment unlock right after your donation."
              : "Booking opens soon. Chat with us to join the early-access list and be the first to know when sessions go live."}
          </p>

          {/* B2B / Institutions */}
          <Reveal delay={0.05}>
            <div data-testid="b2b-tier" className="mt-16 bg-[#041C3E] text-white p-8 sm:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div data-testid="b2b-program-details" className="lg:col-span-7">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">
                    <Building2 size={15} /> For institutes &amp; organizations
                  </div>
                  <h3 className="font-head text-3xl sm:text-4xl font-black mt-4 leading-[1.1]">
                    {inst.heading}
                  </h3>
                  <p className="text-white/70 mt-4 leading-relaxed max-w-xl">
                    {inst.description}
                  </p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {inst.bullets.map((f) => (
                      <li key={f} className="text-sm text-white/80 flex gap-2"><span className="text-[#D94833]">·</span> {f}</li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 lg:border-l lg:border-white/15 lg:pl-10">
                  <p className="text-sm text-white/60 mt-1 leading-relaxed">
                    {inst.note}
                  </p>
                  <div className="mt-6 flex flex-col gap-3">
                    <button data-testid="b2b-enquiry-button" onClick={() => setEnquiryOpen(true)}
                      className="w-full bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors">
                      Inquiry for pricing and partnership
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-[#F7F5F2]" data-testid="landing-testimonials">
        <div className="max-w-7xl mx-auto px-6">
          <TestimonialsSection heading="Testimonials and feedback." limit={3} />
          <div className="mt-10">
            <a href="/testimonials" data-testid="testimonials-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#041C3E] border-b-2 border-[#D94833] pb-1 hover:text-[#D94833] transition-colors">
              Read all testimonials <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#041C3E] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-8">
          <div>
            <p className="font-head font-black text-2xl">The Next Move<span className="text-[#D94833]"> by Rhellex</span></p>
            <p className="text-white/50 text-sm mt-2">Career Strategy &amp; Professional Growth Mentorship</p>
            <div data-testid="footer-contact" className="mt-4">
              <p className="text-white text-sm font-semibold">Contact Rhea</p>
              <a href="mailto:thenextmovebyrhellex@gmail.com" className="text-white/60 text-sm hover:text-white transition-colors">thenextmovebyrhellex@gmail.com</a>
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <SocialLinks tone="dark" />
            <div className="flex items-center gap-4">
              <a data-testid="footer-about-link" href="/about" className="text-white/70 text-sm font-semibold hover:text-white transition-colors">About Rhea</a>
              <a data-testid="footer-blog-link" href="/blog" className="text-white/70 text-sm font-semibold hover:text-white transition-colors">Blog</a>
              <a data-testid="footer-terms-link" href="/terms" className="text-white/70 text-sm font-semibold hover:text-white transition-colors">Terms &amp; Conditions</a>
            </div>
            <p className="text-white/40 text-xs">© {new Date().getFullYear()} The Next Move by Rhellex. All rights reserved.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-8 border-t border-white/10">
          <p data-testid="footer-disclaimer" className="text-white/50 text-xs leading-relaxed max-w-4xl">
            <span className="font-bold text-white/70 uppercase tracking-[0.15em]">Important:</span>{" "}
            The Next Move by Rhellex provides career coaching, mentoring, and guidance only. We do not guarantee, promise, or
            influence admissions, job offers, placements, or any form of intake. Our sole purpose is to guide you
            with clarity and strategy so you can step up and excel in your chosen role. Outcomes depend on your own
            effort, decisions, and circumstances.
          </p>
        </div>
      </footer>

      {activeTier && <PaymentModal tier={activeTier} onClose={() => setActiveTier(null)} />}
      <EnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />

    </div>
  );
}
